import type { Base } from "./_config/index.js"
import { Config } from "./_config/index.js"
import { SendAuthCodeAuthUseCaseInput, SendAuthCodeAuthUseCaseOutput } from "./auth/UseCases/SendAuthCodeAuthUseCase.js"
import { SignInAuthUseCaseInput, SignInAuthUseCaseOutput } from "./auth/UseCases/SignInAuthUseCase.js"
import {
  GetAuthPayloadAuthUseCaseInput,
  GetAuthPayloadAuthUseCaseOutput,
} from "./auth/UseCases/GetAuthPayloadAuthUseCase.js"
import { LogoutAuthUseCaseInput, LogoutAuthUseCaseOutput } from "./auth/UseCases/LogoutAuthUseCase.js"
import {
  GetVotingListVoteUseCaseInput,
  GetVotingListVoteUseCaseOutput,
} from "./vote/UseCases/GetVotingListVoteUseCase.js"
import { CreateVotingVoteUseCaseInput, CreateVotingVoteUseCaseOutput } from "./vote/UseCases/CreateVotingVoteUseCase.js"
import { UpdateVotingVoteUseCaseInput, UpdateVotingVoteUseCaseOutput } from "./vote/UseCases/UpdateVotingVoteUseCase.js"
import { DeleteVotingVoteUseCaseInput, DeleteVotingVoteUseCaseOutput } from "./vote/UseCases/DeleteVotingVoteUseCase.js"
import {
  UpdatePublishedVotingVoteUseCaseInput,
  UpdatePublishedVotingVoteUseCaseOutput,
} from "./vote/UseCases/UpdatePublishedVotingVoteUseCase.js"
import {
  CreateTokenOnBlockchainVoteUseCaseInput,
  CreateTokenOnBlockchainVoteUseCaseOutput,
} from "./vote/UseCases/CreateTokenOnBlockchainVoteUseCase.js"
import {
  SetVoteWindowVoteUseCaseInput,
  SetVoteWindowVoteUseCaseOutput,
} from "./vote/UseCases/SetVoteWindowVoteUseCase.js"
import {
  ToggleAutoUpdatableVotingVoteUseCaseInput,
  ToggleAutoUpdatableVotingVoteUseCaseOutput,
} from "./vote/UseCases/ToggleAutoUpdatableVotingVoteUseCase.js"
import {
  RefreshAutoUpdatableAllowlistsVoteUseCaseInput,
  RefreshAutoUpdatableAllowlistsVoteUseCaseOutput,
} from "./vote/UseCases/RefreshAutoUpdatableAllowlistsVoteUseCase.js"
import {
  GetEntidadesTerritoryUseCaseInput,
  GetEntidadesTerritoryUseCaseOutput,
} from "./territory/UseCases/GetEntidadesTerritoryUseCase.js"
import {
  GetDistritosByEntidadTerritoryUseCaseInput,
  GetDistritosByEntidadTerritoryUseCaseOutput,
} from "./territory/UseCases/GetDistritosByEntidadTerritoryUseCase.js"
import {
  GetMunicipiosByEntidadTerritoryUseCaseInput,
  GetMunicipiosByEntidadTerritoryUseCaseOutput,
} from "./territory/UseCases/GetMunicipiosByEntidadTerritoryUseCase.js"
import {
  GetSeccionesByEntidadAndDistritoTerritoryUseCaseInput,
  GetSeccionesByEntidadAndDistritoTerritoryUseCaseOutput,
} from "./territory/UseCases/GetSeccionesByEntidadAndDistritoTerritoryUseCase.js"
import {
  GetElectoralCatalogBySectionTerritoryUseCaseInput,
  GetElectoralCatalogBySectionTerritoryUseCaseOutput,
} from "./territory/UseCases/GetElectoralCatalogBySectionTerritoryUseCase.js"
import {
  GetPendingTransactionsSafeUseCaseInput,
  GetPendingTransactionsSafeUseCaseOutput,
} from "./safe/UseCases/GetPendingTransactionsSafeUseCase.js"
import type {
  GetSafeTransactionsSafeUseCaseInput,
  GetSafeTransactionsSafeUseCaseOutput,
} from "./safe/UseCases/GetSafeTransactionsSafeUseCase.js"
import {
  GetMonitoringStatusSafeUseCaseInput,
  GetMonitoringStatusSafeUseCaseOutput,
} from "./safe/UseCases/GetMonitoringStatusSafeUseCase.js"
import {
  ExecutePendingSafeTransactionsSafeUseCaseInput,
  ExecutePendingSafeTransactionsSafeUseCaseOutput,
} from "./safe/UseCases/ExecutePendingSafeTransactionsSafeUseCase.js"

export class Domain {
  #config: Config

  static create(config: Base) {
    return new Domain(config)
  }

  constructor(config: Base) {
    this.#config = Config.create(config)
  }

  get config() {
    return this.#config
  }

  /** Auth */
  get SendAuthCodeAuthUseCase() { return this.#getter<SendAuthCodeAuthUseCaseInput, SendAuthCodeAuthUseCaseOutput>(async () => import("./auth/UseCases/SendAuthCodeAuthUseCase.js"), "SendAuthCodeAuthUseCase") } // prettier-ignore
  get SignInAuthUseCase() { return this.#getter<SignInAuthUseCaseInput, SignInAuthUseCaseOutput>(async () => import("./auth/UseCases/SignInAuthUseCase.js"), "SignInAuthUseCase") } // prettier-ignore
  get GetAuthPayloadAuthUseCase() { return this.#getter<GetAuthPayloadAuthUseCaseInput, GetAuthPayloadAuthUseCaseOutput>(async () => import("./auth/UseCases/GetAuthPayloadAuthUseCase.js"), "GetAuthPayloadAuthUseCase") } // prettier-ignore
  get LogoutAuthUseCase() { return this.#getter<LogoutAuthUseCaseInput, LogoutAuthUseCaseOutput>(async () => import("./auth/UseCases/LogoutAuthUseCase.js"), "LogoutAuthUseCase") } // prettier-ignore

  /** Vote */
  get GetVotingListVoteUseCase() { return this.#getter<GetVotingListVoteUseCaseInput, GetVotingListVoteUseCaseOutput>(async () => import("./vote/UseCases/GetVotingListVoteUseCase.js"), "GetVotingListVoteUseCase") } // prettier-ignore
  get CreateVotingVoteUseCase() { return this.#getter<CreateVotingVoteUseCaseInput, CreateVotingVoteUseCaseOutput>(async () => import("./vote/UseCases/CreateVotingVoteUseCase.js"), "CreateVotingVoteUseCase") } // prettier-ignore
  get UpdateVotingVoteUseCase() { return this.#getter<UpdateVotingVoteUseCaseInput, UpdateVotingVoteUseCaseOutput>(async () => import("./vote/UseCases/UpdateVotingVoteUseCase.js"), "UpdateVotingVoteUseCase") } // prettier-ignore
  get DeleteVotingVoteUseCase() { return this.#getter<DeleteVotingVoteUseCaseInput, DeleteVotingVoteUseCaseOutput>(async () => import("./vote/UseCases/DeleteVotingVoteUseCase.js"), "DeleteVotingVoteUseCase") } // prettier-ignore
  get UpdatePublishedVotingVoteUseCase() { return this.#getter<UpdatePublishedVotingVoteUseCaseInput, UpdatePublishedVotingVoteUseCaseOutput>(async () => import("./vote/UseCases/UpdatePublishedVotingVoteUseCase.js"), "UpdatePublishedVotingVoteUseCase") } // prettier-ignore
  get CreateTokenOnBlockchainVoteUseCase() { return this.#getter<CreateTokenOnBlockchainVoteUseCaseInput, CreateTokenOnBlockchainVoteUseCaseOutput>(async () => import("./vote/UseCases/CreateTokenOnBlockchainVoteUseCase.js"), "CreateTokenOnBlockchainVoteUseCase") } // prettier-ignore
  get SetVoteWindowVoteUseCase() { return this.#getter<SetVoteWindowVoteUseCaseInput, SetVoteWindowVoteUseCaseOutput>(async () => import("./vote/UseCases/SetVoteWindowVoteUseCase.js"), "SetVoteWindowVoteUseCase") } // prettier-ignore
  get ToggleAutoUpdatableVotingVoteUseCase() { return this.#getter<ToggleAutoUpdatableVotingVoteUseCaseInput, ToggleAutoUpdatableVotingVoteUseCaseOutput>(async () => import("./vote/UseCases/ToggleAutoUpdatableVotingVoteUseCase.js"), "ToggleAutoUpdatableVotingVoteUseCase") } // prettier-ignore
  get RefreshAutoUpdatableAllowlistsVoteUseCase() { return this.#getter<RefreshAutoUpdatableAllowlistsVoteUseCaseInput, RefreshAutoUpdatableAllowlistsVoteUseCaseOutput>(async () => import("./vote/UseCases/RefreshAutoUpdatableAllowlistsVoteUseCase.js"), "RefreshAutoUpdatableAllowlistsVoteUseCase") } // prettier-ignore

  /** Territory */
  get GetEntidadesTerritoryUseCase() { return this.#getter<GetEntidadesTerritoryUseCaseInput, GetEntidadesTerritoryUseCaseOutput>(async () => import("./territory/UseCases/GetEntidadesTerritoryUseCase.js"), "GetEntidadesTerritoryUseCase") } // prettier-ignore
  get GetDistritosByEntidadTerritoryUseCase() { return this.#getter<GetDistritosByEntidadTerritoryUseCaseInput, GetDistritosByEntidadTerritoryUseCaseOutput>(async () => import("./territory/UseCases/GetDistritosByEntidadTerritoryUseCase.js"), "GetDistritosByEntidadTerritoryUseCase") } // prettier-ignore
  get GetMunicipiosByEntidadTerritoryUseCase() { return this.#getter<GetMunicipiosByEntidadTerritoryUseCaseInput, GetMunicipiosByEntidadTerritoryUseCaseOutput>(async () => import("./territory/UseCases/GetMunicipiosByEntidadTerritoryUseCase.js"), "GetMunicipiosByEntidadTerritoryUseCase") } // prettier-ignore
  get GetSeccionesByEntidadAndDistritoTerritoryUseCase() { return this.#getter<GetSeccionesByEntidadAndDistritoTerritoryUseCaseInput, GetSeccionesByEntidadAndDistritoTerritoryUseCaseOutput>(async () => import("./territory/UseCases/GetSeccionesByEntidadAndDistritoTerritoryUseCase.js"), "GetSeccionesByEntidadAndDistritoTerritoryUseCase") } // prettier-ignore
  get GetElectoralCatalogBySectionTerritoryUseCase() { return this.#getter<GetElectoralCatalogBySectionTerritoryUseCaseInput, GetElectoralCatalogBySectionTerritoryUseCaseOutput>(async () => import("./territory/UseCases/GetElectoralCatalogBySectionTerritoryUseCase.js"), "GetElectoralCatalogBySectionTerritoryUseCase") } // prettier-ignore

  /** Safe */
  get GetPendingTransactionsSafeUseCase() { return this.#getter<GetPendingTransactionsSafeUseCaseInput, GetPendingTransactionsSafeUseCaseOutput>(async () => import("./safe/UseCases/GetPendingTransactionsSafeUseCase.js"), "GetPendingTransactionsSafeUseCase") } // prettier-ignore
  get GetSafeTransactionsSafeUseCase() { return this.#getter<GetSafeTransactionsSafeUseCaseInput, GetSafeTransactionsSafeUseCaseOutput>(async () => import("./safe/UseCases/GetSafeTransactionsSafeUseCase.js"), "GetSafeTransactionsSafeUseCase") } // prettier-ignore
  get GetMonitoringStatusSafeUseCase() { return this.#getter<GetMonitoringStatusSafeUseCaseInput, GetMonitoringStatusSafeUseCaseOutput>(async () => import("./safe/UseCases/GetMonitoringStatusSafeUseCase.js"), "GetMonitoringStatusSafeUseCase") } // prettier-ignore
  get ExecutePendingSafeTransactionsSafeUseCase() { return this.#getter<ExecutePendingSafeTransactionsSafeUseCaseInput, ExecutePendingSafeTransactionsSafeUseCaseOutput>(async () => import("./safe/UseCases/ExecutePendingSafeTransactionsSafeUseCase.js"), "ExecutePendingSafeTransactionsSafeUseCase") } // prettier-ignore

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  #getter<I, O>(loader: Function, name: string) {
    return {
      execute: async (input: I): Promise<O> => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const Klass = await loader().then((mod) => mod[name])
        const uc = Klass.create({ config: this.#config })

        return uc.execute(input) as O
      },
    }
  }
}
